import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';

const ExchangeRatesContext = createContext(null);

export const ExchangeRatesProvider = ({ children }) => {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Load rates from backend (cached or fresh)
  const loadRates = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const response = await api.get('/exchange-rates/current');
      if (response.data) {
        setRates(response.data);
        setLastUpdate(new Date(response.data.fetched_at));
        setInitialized(true);
      }
    } catch (err) {
      console.error('Failed to load exchange rates:', err);
      if (!silent && err.response?.status !== 403) {
        setError('Döviz kurları yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh rates from TCMB
  const refreshRates = useCallback(async (showToast = true) => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await api.post('/exchange-rates/refresh');
      if (response.data?.rates) {
        setRates(response.data.rates);
        setLastUpdate(new Date(response.data.rates.fetched_at));
        if (showToast) {
          toast.success('Döviz kurları TCMB\'den güncellendi');
        }
        return response.data.rates;
      }
    } catch (err) {
      console.error('Failed to refresh exchange rates:', err);
      if (showToast) {
        toast.error('Döviz kurları güncellenemedi');
      }
      setError('Döviz kurları güncellenemedi');
      // Try loading cached rates
      await loadRates(true);
    } finally {
      setRefreshing(false);
    }
    return null;
  }, [loadRates]);

  // Get specific currency rate
  const getRate = useCallback((currency, type = 'forex_buying') => {
    if (!rates?.currencies?.[currency]) {
      return null;
    }
    return rates.currencies[currency][type] || null;
  }, [rates]);

  // Get USD rate (commonly used)
  const getUsdRate = useCallback((type = 'forex_buying') => {
    return getRate('USD', type) || 42.0; // fallback
  }, [getRate]);

  // Get EUR rate
  const getEurRate = useCallback((type = 'forex_buying') => {
    return getRate('EUR', type) || 45.0; // fallback
  }, [getRate]);

  // Get GBP rate
  const getGbpRate = useCallback((type = 'forex_buying') => {
    return getRate('GBP', type) || 52.0; // fallback
  }, [getRate]);

  // Convert amount between currencies
  const convert = useCallback((amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    
    if (fromCurrency === 'TRY') {
      const rate = getRate(toCurrency, 'forex_selling');
      return rate ? amount / rate : amount;
    } else if (toCurrency === 'TRY') {
      const rate = getRate(fromCurrency, 'forex_buying');
      return rate ? amount * rate : amount;
    } else {
      // Convert via TRY
      const tryAmount = convert(amount, fromCurrency, 'TRY');
      return convert(tryAmount, 'TRY', toCurrency);
    }
  }, [getRate]);

  // Initial load - only when there's a token
  useEffect(() => {
    // Check if user is logged in by checking for token
    const token = localStorage.getItem('token');
    if (token && !initialized) {
      loadRates();
    }
    
    // Auto-refresh every 30 minutes (if logged in)
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem('token');
      if (currentToken && initialized) {
        loadRates(true);
      }
    }, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [loadRates, initialized]);

  const value = {
    rates,
    loading,
    refreshing,
    lastUpdate,
    error,
    initialized,
    loadRates,
    refreshRates,
    getRate,
    getUsdRate,
    getEurRate,
    getGbpRate,
    convert
  };

  return (
    <ExchangeRatesContext.Provider value={value}>
      {children}
    </ExchangeRatesContext.Provider>
  );
};

export const useExchangeRates = () => {
  const context = useContext(ExchangeRatesContext);
  if (!context) {
    throw new Error('useExchangeRates must be used within ExchangeRatesProvider');
  }
  return context;
};

export default ExchangeRatesContext;
